/**
 * Add to Cart Selectors
 */

/**
 * Internal dependencies
 */
import type {
	AddToCartState,
	ProductInstanceState,
} from "../../types/add-to-cart.types";

export const getInstance = (
	state: AddToCartState,
	productId: number,
): ProductInstanceState | undefined => {
	return state.instances[productId];
};

export const getQuantity = (
	state: AddToCartState,
	productId: number,
): number => {
	return state.instances[productId]?.quantity ?? 1;
};

export const getVariants = (state: AddToCartState, productId: number) => {
	return state.instances[productId]?.variants ?? [];
};

export const getFees = (state: AddToCartState, productId: number) => {
	return state.instances[productId]?.fees ?? [];
};

export const getSelectedVariantId = (
	state: AddToCartState,
	productId: number,
): null | number => {
	return state.instances[productId]?.selectedVariantId ?? null;
};

export const getSelectedVariant = (
	state: AddToCartState,
	productId: number,
) => {
	const instance = state.instances[productId];
	if (!instance?.selectedVariantId) return null;

	return (
		instance.variants.find(
			(variant) => variant.id === instance.selectedVariantId,
		) ?? null
	);
};

export const getValidationErrors = (
	state: AddToCartState,
	productId: number,
) => {
	return state.instances[productId]?.validationErrors ?? {};
};

export const getError = (
	state: AddToCartState,
	productId: number,
): null | string => {
	return state.instances[productId]?.error ?? null;
};

export const isLoadingVariants = (
	state: AddToCartState,
	productId: number,
): boolean => {
	return state.instances[productId]?.isLoadingVariants ?? false;
};

export const isAdding = (state: AddToCartState, productId: number): boolean => {
	return state.instances[productId]?.isAdding ?? false;
};

export const getSuccessMessage = (
	state: AddToCartState,
	productId: number,
): null | string => {
	return state.instances[productId]?.successMessage ?? null;
};

export const getFormattedTotal = (
	state: AddToCartState,
	productId: number,
): string => {
	const instance = state.instances[productId];
	if (!instance) return "$0.00";

	const selectedVariant = instance.variants.find(
		(variant) => variant.id === instance.selectedVariantId,
	);

	if (!selectedVariant) {
		return instance.formattedPrice;
	}

	const total = selectedVariant.price_amount * instance.quantity;
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: instance.currency,
	}).format(total / 100);
};
